import { useEffect, useMemo, useRef } from 'react'
import { PageBootSkeleton } from '@/components/layout/PageBootSkeleton'
import { ModuleAccessState } from '@/components/shared/empty-states/AccessState'
import { ErrorState } from '@/components/shared/ErrorState'
import { useMyHostOrgsQuery } from '@/hooks/queries/useExternalOrgsQuery'
import { useOrganizationsQuery } from '@/hooks/queries/useOrganizationsQuery'
import { useOrganizationSubscription } from '@/hooks/useOrganizationSubscription'
import { dismissBootSplash } from '@/lib/boot-splash'
import { syncAccountTypeTheme } from '@/lib/personal-theme'
import { useAuth } from '@/store/auth.store'
import { useOrganizationStore } from '@/store/organization.store'
import { useUserProfileStore } from '@/store/user-profile.store'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'

import { resolveOrganizationRouteAccess } from './organization-route-access'

/** Resolve refreshed access before mounting any workspace or admin page. */
export function OrgRouteGuard() {
  const { orgSlug } = useParams<{ orgSlug: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const {
    currentOrganization,
    externalOrgRelationship,
    setCurrentOrganization,
  } = useOrganizationStore()
  const { isAuthenticated } = useAuth()
  const organizations = useOrganizationsQuery({ enabled: isAuthenticated })
  const hostOrgs = useMyHostOrgsQuery()
  const { resetOrganizationData } = useUserProfileStore()
  const previousSlug = useRef<string | null>(null)
  const subscriptionValidated = useRef<string | null>(null)
  const isAdminRoute = /\/admin(\/|$)/.test(location.pathname)
  const isIntegrationsAdminRoute = /\/admin\/integrations(\/|$)/.test(
    location.pathname,
  )
  const isSubscriptionRoute = location.pathname.includes('/subscription')
  const accessUnavailable = organizations.isError || hostOrgs.isError
  const isPending =
    !isAuthenticated ||
    organizations.isPending ||
    hostOrgs.isPending ||
    !hostOrgs.isFetched
  const access = useMemo(() => {
    if (!orgSlug || isPending || accessUnavailable) return null
    return resolveOrganizationRouteAccess(
      orgSlug,
      currentOrganization,
      externalOrgRelationship,
      organizations.data ?? [],
      hostOrgs.data ?? [],
    )
  }, [
    orgSlug,
    isPending,
    accessUnavailable,
    currentOrganization,
    externalOrgRelationship,
    organizations.data,
    hostOrgs.data,
  ])
  const isOrgReady =
    !!access &&
    currentOrganization?.id === access.organization.id &&
    currentOrganization?.slug === orgSlug &&
    !!currentOrganization?.isExternalAccess ===
      !!access.organization.isExternalAccess &&
    JSON.stringify(currentOrganization?.userMembership) ===
      JSON.stringify(access.organization.userMembership) &&
    JSON.stringify(externalOrgRelationship) ===
      JSON.stringify(access.relationship)
  const membership = access?.organization.userMembership
  const isAdminDenied =
    isAdminRoute &&
    (!access ||
      access.organization.isExternalAccess ||
      (!isIntegrationsAdminRoute &&
        membership?.role !== 'OWNER' &&
        membership?.role !== 'ADMIN'))
  const {
    needsSubscription,
    isLoading: isSubLoading,
    isFetched: isSubFetched,
    error: subscriptionError,
  } = useOrganizationSubscription(currentOrganization?.id, {
    enabled: isOrgReady,
  })

  useEffect(() => {
    if (orgSlug && orgSlug !== previousSlug.current) {
      resetOrganizationData()
      previousSlug.current = orgSlug
      subscriptionValidated.current = null
    }
  }, [orgSlug, resetOrganizationData])

  useEffect(() => {
    if (!orgSlug) {
      navigate('/organizations', { replace: true })
      return
    }
    if (isPending || accessUnavailable) return
    if (access) {
      setCurrentOrganization(access.organization, access.relationship)
    } else if (
      currentOrganization?.slug === orgSlug &&
      (currentOrganization.userMembership || externalOrgRelationship)
    ) {
      // Keep the selected mode, so clearing a revoked grant cannot select an
      // unrelated direct membership or partner relationship on the next render.
      setCurrentOrganization(
        { ...currentOrganization, userMembership: null },
        null,
      )
    }
  }, [
    orgSlug,
    isPending,
    accessUnavailable,
    access,
    currentOrganization,
    externalOrgRelationship,
    setCurrentOrganization,
    navigate,
  ])

  useEffect(() => {
    if (isOrgReady) syncAccountTypeTheme(currentOrganization?.accountType)
  }, [isOrgReady, currentOrganization?.accountType])

  useEffect(() => {
    // Recovery and denial screens are destinations too: never hide them
    // behind the desktop boot splash.
    if (
      accessUnavailable ||
      (!isPending && !!orgSlug && !access) ||
      isOrgReady
    ) {
      dismissBootSplash({ waitForGlass: true })
    }
  }, [accessUnavailable, isPending, orgSlug, access, isOrgReady])

  useEffect(() => {
    if (
      !isOrgReady ||
      !currentOrganization ||
      isSubscriptionRoute ||
      isAdminRoute ||
      isSubLoading ||
      !isSubFetched ||
      subscriptionError
    )
      return
    if (subscriptionValidated.current === currentOrganization.id) return
    subscriptionValidated.current = currentOrganization.id
    if (needsSubscription)
      navigate(`/${currentOrganization.slug}/subscription-setup`, {
        replace: true,
      })
  }, [
    isOrgReady,
    currentOrganization,
    isSubscriptionRoute,
    isAdminRoute,
    isSubLoading,
    isSubFetched,
    subscriptionError,
    needsSubscription,
    navigate,
  ])

  if (!orgSlug) return null
  if (accessUnavailable)
    return (
      <ErrorState
        title="Workspace access couldn’t be checked"
        description="Your workspace selection is saved. Try again to check your access."
        onRetry={() => {
          void Promise.all([organizations.refetch(), hostOrgs.refetch()]).catch(
            () => {},
          )
        }}
        onBack={() => navigate('/organizations')}
        backLabel="Choose workspace"
      />
    )
  if (isPending) return <PageBootSkeleton />
  if (!access)
    return (
      <ErrorState
        title="Workspace access is unavailable"
        description="Ask a workspace administrator to check your access, or choose another workspace."
        onBack={() => navigate('/organizations')}
        backLabel="Choose workspace"
      />
    )
  if (!isOrgReady) return <PageBootSkeleton />
  if (isAdminDenied)
    return <ModuleAccessState moduleSlug="admin" title="Admin" />
  return <Outlet />
}
